import React, { Component } from 'react';
import { connect, ResolveThunks } from 'react-redux';
import { Box, Toolbar } from '@mui/material';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import Typography from '@mui/material/Typography';

import {
  applySelectedTemplateToActivePage,
  applySelectedTemplateToActivePageWithoutTranslation,
  applySelectedTemplateToAllPagesInPdf,
  applySelectedTemplateToFirstPageOfEveryComponentPdf,
} from '../../actions/annotationTools';
import { getSortedAnnotationTemplates } from '../../selectors/annotationTemplates';
import { selectAnnotationTemplate } from '../../store/annotationTemplates';
import { viewAvailableTemplatesOpenToggle } from '../../store/view';
import TemplateDirectory from './TemplateDirectory';

const buttonSx = {
  my: 1,
  py: 1,
  width: '100%',
  bgcolor: 'backContrast.main',
  '&:hover': {
    bgcolor: 'primary.dark',
    color: '#fff',
  },
} as const;

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ResolveThunks<typeof actionCreators>;

class AnnotationDrawer extends Component<StateProps & DispatchProps> {
  toggleDrawer = () => {
    this.props.viewAvailableTemplatesOpenToggle();
  };

  selectAnnotationTemplate = (template: any) => {
    this.props.selectAnnotationTemplate(template);
  };

  render() {
    return (
      <Drawer
        variant="persistent"
        sx={{
          '& .MuiDrawer-paper': {
            flexShrink: 0,
            zIndex: 1000,
            width: 400,
            position: 'relative',
            bgcolor: 'drawerDialog.main',
            pl: 3,
            pr: 4,
            py: 2,
            display: 'flex',
            overflowY: 'hidden',
            '&:hover': {
              overflowY: 'auto',
            },
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            scrollbarWidth: 'none',
          },
        }}
        anchor="right"
        open={this.props.isOpen}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Toolbar />
          <Box sx={{ mb: 'auto' }}>
            <TemplateDirectory
              templates={this.props.templates}
              selectedTemplate={this.props.selectedTemplate ? this.props.selectedTemplate.id : null}
              onSelectTemplate={this.selectAnnotationTemplate}
            />
          </Box>
          <Box sx={{ width: '100%', textAlign: 'left', mt: 2 }}>
            <Typography variant="caption">Apply Selected Template</Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                mt: 1,
                mb: 2,
                border: 'solid 1px',
                borderColor: 'backContrast.main',
                p: 1,
              }}
            >
              <Button variant="outlined" sx={buttonSx} onClick={this.props.applySelectedTemplateToActivePage}>
                To This Page
              </Button>
              <Button
                variant="outlined"
                sx={buttonSx}
                onClick={this.props.applySelectedTemplateToActivePageWithoutTranslation}
              >
                Without Translation
              </Button>
              <Button variant="outlined" sx={buttonSx} onClick={this.props.applySelectedTemplateToAllPagesInPdf}>
                To This PDF&apos;s Pages
              </Button>
              <Button
                variant="outlined"
                sx={buttonSx}
                onClick={this.props.applySelectedTemplateToFirstPageOfEveryComponentPdf}
              >
                To First Page of Every Component
              </Button>
            </Box>
            <Button variant="outlined" fullWidth onClick={this.toggleDrawer}>
              Close
            </Button>
          </Box>
        </Box>
      </Drawer>
    );
  }
}

const mapStateToProps = (state: any) => ({
  isOpen: state.view.controlPanelVisibility.isAvailableTemplatesOpen,
  selectedTemplate: state.annotationTemplates.annotationTemplates[state.annotationTemplates.selectedTemplate],
  templates: getSortedAnnotationTemplates(state),
});

const actionCreators = {
  viewAvailableTemplatesOpenToggle,
  selectAnnotationTemplate,
  applySelectedTemplateToActivePage,
  applySelectedTemplateToActivePageWithoutTranslation,
  applySelectedTemplateToAllPagesInPdf,
  applySelectedTemplateToFirstPageOfEveryComponentPdf,
};

export default connect(mapStateToProps, actionCreators)(AnnotationDrawer);
